import { useEffect, useRef, useState } from 'react';
import { Footer } from '../ui/Footer';

interface LandingPageProps {
  onLoginClick: () => void;
  onRegisterClick: () => void;
  isAuthenticated?: boolean;
}

export function LandingPage({ onLoginClick, onRegisterClick, isAuthenticated = false }: LandingPageProps) {
  const [currentSection, setCurrentSection] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);
  const totalSections = isAuthenticated ? 5 : 1; // 메인 + Why + What + How + Future

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleWheel = (e: WheelEvent) => {
      if (isScrolling.current) return;

      e.preventDefault();

      if (e.deltaY > 0 && currentSection < totalSections - 1) {
        // 아래로 스크롤
        isScrolling.current = true;
        setCurrentSection(prev => prev + 1);
        setTimeout(() => { isScrolling.current = false; }, 1000);
      } else if (e.deltaY < 0 && currentSection > 0) {
        // 위로 스크롤
        isScrolling.current = true;
        setCurrentSection(prev => prev - 1);
        setTimeout(() => { isScrolling.current = false; }, 1000);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [currentSection, totalSections, isAuthenticated]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;600;700;800;900&display=swap');

        .fullpage-container {
          width: 100%;
          height: 100vh;
          overflow: hidden;
          position: relative;
        }

        .sections-wrapper {
          width: 100%;
          height: 100%;
          transition: transform 1s cubic-bezier(0.77, 0, 0.175, 1);
        }

        .landing-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          height: 100vh;
          padding: 2rem;
          text-align: center;
          width: 100%;
        }

        .detail-page {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          height: 100vh;
          padding: 8rem 2rem 8rem;
          width: 100%;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .landing-title {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 3.5rem;
          font-weight: 900;
          color: #ffffff;
          margin-bottom: 1rem;
          line-height: 1.2;
          text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .landing-subtitle {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 2rem;
          font-weight: 600;
          color: #f0f0f0;
          margin-bottom: 1.5rem;
          line-height: 1.3;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .landing-description {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.125rem;
          font-weight: 400;
          color: #e8e8e8;
          margin-bottom: 3rem;
          max-width: 900px;
          line-height: 1.6;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .landing-buttons {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          align-items: center;
        }

        .landing-btn {
          padding: 1.25rem 3rem;
          font-size: 1.125rem;
          font-weight: 600;
          font-family: 'Noto Sans KR', sans-serif;
          border: none;
          border-radius: 40px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .landing-btn-login {
          background: rgba(255, 255, 255, 0.95);
          color: #1a1a1a;
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .landing-btn-login:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 1);
        }

        .landing-btn-register {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          color: #ffffff !important;
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .landing-btn-register:hover {
          transform: translateY(-2px);
          background: rgba(255, 255, 255, 0.25);
          border-color: rgba(255, 255, 255, 0.5);
          box-shadow: 0 12px 32px rgba(255, 255, 255, 0.2);
          color: #ffffff !important;
        }

        .landing-btn:active {
          transform: translateY(0);
        }

        .scroll-indicator {
          position: absolute;
          bottom: 4rem;
          left: 50%;
          transform: translateX(-50%);
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.875rem;
          font-family: 'Noto Sans KR', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          animation: bounce 2s infinite;
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          40% {
            transform: translateX(-50%) translateY(-10px);
          }
          60% {
            transform: translateX(-50%) translateY(-5px);
          }
        }

        .details-content {
          background: transparent;
          border-radius: 0;
          padding: 0;
          max-width: 1000px;
          width: 100%;
          margin: 0 auto;
        }

        .details-header {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.375rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.85);
          margin-bottom: 4rem;
          text-align: center;
          padding-bottom: 0;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          line-height: 1.6;
        }

        .detail-section {
          margin-bottom: 4rem;
        }

        .detail-title {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 3rem;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 3rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          text-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
          line-height: 1.2;
        }

        .detail-subtitle {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          line-height: 1.5;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .detail-text {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.125rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.9);
          line-height: 2;
          margin-bottom: 1.5rem;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .detail-list {
          margin-left: 0;
          margin-top: 1rem;
          margin-bottom: 2rem;
        }

        .detail-list li {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.0625rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.85);
          line-height: 2;
          margin-bottom: 1rem;
          position: relative;
          padding-left: 1.75rem;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        .detail-list li:before {
          content: "•";
          position: absolute;
          left: 0;
          color: rgba(255, 255, 255, 0.7);
          font-weight: bold;
          font-size: 1.25rem;
        }

        .detail-list li strong {
          font-weight: 700;
          color: #ffffff;
        }

        .hero-message {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 2.25rem;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 2rem;
          line-height: 1.4;
          text-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
        }

        .sub-message {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.25rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          line-height: 1.6;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        .description-text {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.125rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.85);
          line-height: 1.9;
          margin-bottom: 1rem;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        .use-case-box {
          background: rgba(255, 255, 255, 0.08);
          border-left: 5px solid rgba(255, 255, 255, 0.6);
          padding: 2.5rem;
          margin: 3rem 0;
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .limitation-box {
          background: rgba(255, 193, 7, 0.12);
          border-left: 5px solid #FFC107;
          padding: 2.5rem;
          margin: 3rem 0;
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        @media (max-width: 768px) {
          .landing-title {
            font-size: 2.5rem;
          }

          .landing-subtitle {
            font-size: 1.5rem;
          }

          .landing-description {
            font-size: 1rem;
          }

          .landing-buttons {
            flex-direction: column;
            width: 100%;
            max-width: 300px;
          }

          .landing-btn {
            width: 100%;
          }

          .detail-page {
            padding: 6rem 1.5rem 5rem;
          }

          .details-content {
            padding: 0;
          }

          .details-header {
            font-size: 1.125rem;
            margin-bottom: 3rem;
          }

          .detail-title {
            font-size: 2rem;
            margin-bottom: 2rem;
          }

          .detail-subtitle {
            font-size: 1.25rem;
            margin-top: 2rem;
            margin-bottom: 1rem;
          }

          .detail-text {
            font-size: 1rem;
            line-height: 1.8;
          }

          .detail-list li {
            font-size: 0.9375rem;
            padding-left: 1.5rem;
            line-height: 1.8;
          }

          .use-case-box, .limitation-box {
            padding: 1.5rem;
            margin: 2rem 0;
          }
        }

        .section-indicators {
          position: fixed;
          right: 2rem;
          top: 50%;
          transform: translateY(-50%);
          z-index: 100;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .section-indicator {
          display: flex;
          align-items: center;
          gap: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .section-indicator-dot {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.4);
          transition: all 0.3s ease;
        }

        .section-indicator.active .section-indicator-dot {
          width: 18px;
          height: 18px;
          background: white;
          box-shadow: 0 0 15px rgba(255, 255, 255, 0.7);
        }

        .section-indicator-label {
          font-family: 'Noto Sans KR', sans-serif;
          font-size: 1.2rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.5);
          opacity: 0;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .section-indicator:hover .section-indicator-label,
        .section-indicator.active .section-indicator-label {
          opacity: 1;
          color: rgba(255, 255, 255, 0.95);
        }

        @media (max-width: 768px) {
          .section-indicators {
            right: 1rem;
          }

          .section-indicator-label {
            display: none;
          }
        }

      `}</style>

      <div className="fullpage-container" ref={containerRef}>
        <div
          className="sections-wrapper"
          style={{ transform: `translateY(-${currentSection * 100}vh)` }}
        >
          {/* Section 0: Main Landing */}
          <div className="landing-container">
            <h1 className="landing-title">
              신속하고 정확하게<br />클라우드를 관리할 수 있도록
            </h1>

            <h2 className="landing-subtitle">
              자연어로 클라우드와 고객 데이터를 관리합니다
            </h2>

            <p className="landing-description">
              WIZ OCM은 클라우드 환경 모니터링을 시작으로,<br></br>
              클라우드 환경을 자연어로 제어하는 AI-Driven Cloud Management Platform으로 나아가고자 합니다.
            </p>

            <div className="landing-buttons">
              {isAuthenticated ? (
                <>
                  <button className="landing-btn landing-btn-login" onClick={() => setCurrentSection(1)}>
                    Why, What, How?
                  </button>
                  <button className="landing-btn landing-btn-register" onClick={() => setCurrentSection(4)}>
                    Future
                  </button>
                </>
              ) : (
                <>
                  <button className="landing-btn landing-btn-login" onClick={onLoginClick}>
                    로그인
                  </button>
                  <button className="landing-btn landing-btn-register" onClick={onRegisterClick}>
                    회원가입 요청
                  </button>
                </>
              )}
            </div>

            {isAuthenticated && currentSection === 0 && (
              <div className="scroll-indicator">
                <span>스크롤하여 자세히 보기</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            )}
          </div>

          {/* Section 1: Why */}
          {isAuthenticated && (
            <div className="detail-page">
              <div className="details-content">
                <div className="detail-section">
                  <div className="hero-message">
                    클라우드 관리,<br />
                    더 쉽고 빠르게 할 수 없을까?
                  </div>

                  <div className="sub-message">고객은 클라우드에 익숙하지 않습니다</div>
                  <p className="description-text">
                    구현에 어려움을 겪고,<br />
                    다양한 옵션을 모두 파악하지 못해 서비스를 100% 활용하지 못합니다.<br />
                  </p>

                  <div className="sub-message">엔지니어는 반복적인 응대로 시간을 낭비합니다</div>
                  <p className="description-text">
                    같은 문의를 고객사별로 반복 대응하다 보니,<br />
                    더 생산적인 일에 집중할 시간이 부족합니다.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section 2: What */}
          {isAuthenticated && (
            <div className="detail-page">
              <div className="details-content">
                <div className="detail-section">
                  <div className="hero-message">
                    AI 챗봇으로<br />
                    클라우드를 자연어로 제어합니다
                  </div>

                  <div className="sub-message">고객에게 제공하는 가치</div>
                  <p className="description-text">
                    추상적인 문의도 AI가 구체적으로 되물어 정확한 요구사항을 파악하고,<br />
                    고려해야 할 모든 옵션을 안내하여 최적의 구현을 제공합니다.
                  </p>
                  <p className="description-text">
                    24시간 365일 즉시 응답받고,<br />
                    자연어만으로 클라우드 환경을 쉽게 제어할 수 있습니다.
                  </p>

                  <div className="sub-message">엔지니어가 얻는 효율</div>
                  <p className="description-text">
                    반복적인 문의는 AI가 처리하여 시간을 절약하고,<br />
                    고객 피드백을 데이터화하여 더 나은 솔루션을 만듭니다.
                  </p>
                  <p className="description-text">
                    차별화된 AI 솔루션으로<br />
                    자신 있게 MSP를 제안할 수 있습니다.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section 3: How */}
          {isAuthenticated && (
            <div className="detail-page">
              <div className="details-content">
                <div className="detail-section">
                  <div className="hero-message">
                    안전하고 확장 가능한<br />
                    이중 서버 아키텍처
                  </div>

                  <div className="sub-message">CentralServer - 중앙 관제 시스템</div>
                  <p className="description-text">
                    모든 고객사 데이터를 통합 모니터링하고,<br />
                    AI 모델을 학습시켜 더 나은 답변을 제공합니다.
                  </p>

                  <div className="sub-message">EdgeServer - 고객 전용 환경</div>
                  <p className="description-text">
                    각 고객사마다 완전히 격리된 환경에서 운영되어,<br />
                    데이터 침해 걱정 없이 안전하게 챗봇을 사용할 수 있습니다.
                  </p>

                  <div className="sub-message">동작 방식</div>
                  <p className="description-text">
                    고객이 EdgeServer에서 자연어로 문의하면 CentralServer AI가 Vector 검색을 통해 답변합니다.<br />
                    엔지니어는 AI 응답을 검토하여 확인하거나 추가 안내를 제공하고,<br />
                    모든 대화는 피드백 데이터로 축적되어 AI가 계속 똑똑해집니다.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section 4: Future */}
          {isAuthenticated && (
            <div className="detail-page" style={{ justifyContent: 'flex-start', paddingBottom: 0 }}>
              <div className="details-content">
                <div className="detail-section">
                  <div className="hero-message" style={{ marginBottom: '1.5rem' }}>
                    더 나은 업무 환경을 위해<br />
                    함께 만들어갑니다
                  </div>

                  <div className="use-case-box" style={{ padding: '1.5rem', margin: '1.5rem 0' }}>
                    <div className="sub-message" style={{ marginBottom: '0.5rem' }}>빠른 개발과 지속적인 개선</div>
                    <p className="description-text" style={{ marginBottom: '0', lineHeight: '1.6' }}>
                      업무에 빠르게 적용하고 피드백을 지속적으로 개선해 나갈 예정입니다.
                    </p>
                  </div>

                  <div className="limitation-box" style={{ padding: '1.5rem', margin: '1.5rem 0' }}>
                    <div className="sub-message" style={{ marginBottom: '0.75rem' }}>한계를 인지하고 대비합니다</div>
                    <p className="description-text" style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
                      <strong>멀티 클라우드:</strong> 현재는 Oracle Cloud 기반이지만 다른 플랫폼 확장 가능하도록 설계
                    </p>
                    <p className="description-text" style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
                      <strong>안전한 운영:</strong> AI가 작업을 제안하면 고객이 검토 후 실행하는 방식으로 오작동 방지
                    </p>
                    <p className="description-text" style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
                      <strong>경쟁 대비:</strong> 클라우드 MCP 솔루션 시장에 가장 빠르게 서비스하여, 실제 사용 데이터 선점 및 차별점 유지
                    </p>
                    <p className="description-text" style={{ marginBottom: '0', lineHeight: '1.6' }}>
                      <strong>급변하는 IT:</strong> 지속적인 스터디, 외부 강연, 학습을 통해 최신 기술을 살피고 적용합니다.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer는 마지막 섹션에만 표시 */}
              <div style={{ width: '100%', marginTop: 'auto' }}>
                <Footer transparent={true} />
              </div>
            </div>
          )}
        </div>

        {/* Section Indicators */}
        {isAuthenticated && (
          <div className="section-indicators">
            <div
              className={`section-indicator ${currentSection === 0 ? 'active' : ''}`}
              onClick={() => setCurrentSection(0)}
            >
              <div className="section-indicator-dot"></div>
              <span className="section-indicator-label">Home</span>
            </div>
            <div
              className={`section-indicator ${currentSection === 1 ? 'active' : ''}`}
              onClick={() => setCurrentSection(1)}
            >
              <div className="section-indicator-dot"></div>
              <span className="section-indicator-label">Why?</span>
            </div>
            <div
              className={`section-indicator ${currentSection === 2 ? 'active' : ''}`}
              onClick={() => setCurrentSection(2)}
            >
              <div className="section-indicator-dot"></div>
              <span className="section-indicator-label">What?</span>
            </div>
            <div
              className={`section-indicator ${currentSection === 3 ? 'active' : ''}`}
              onClick={() => setCurrentSection(3)}
            >
              <div className="section-indicator-dot"></div>
              <span className="section-indicator-label">How?</span>
            </div>
            <div
              className={`section-indicator ${currentSection === 4 ? 'active' : ''}`}
              onClick={() => setCurrentSection(4)}
            >
              <div className="section-indicator-dot"></div>
              <span className="section-indicator-label">Future</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
